/**
 * 图层命令（DESIGN.md §4.5）。
 *
 * 全部是**结构性命令**：逆操作只存参数，不碰像素。§4.5 要求
 * "结构性操作的历史条目内存开销 < 1KB"，这里每条的 cost 都在几十字节量级。
 *
 * 每条命令的 `invert` 都必须在 apply **之前**读取当前文档——
 * 删除图层的逆操作要知道被删的是什么、原来在哪个位置，改属性的逆操作
 * 要知道旧值。这是 Command 接口把 `invert(document)` 设计成接受文档的原因。
 */
import type { Command, Json } from '../command-bus';
import { findLayer } from '../../document/draft';
import type { BlendMode, Document, Layer, LayerEffect, LayerId, Mat3, RasterLayer } from '../../document/types';

/** 结构性命令的成本量级：几十字节的参数，远低于 §4.5 的 1KB 上限。 */
const STRUCTURAL_COST = 128;

/** 在文档里找图层的父组 id。根组的直接子图层返回 null。 */
function parentIdOf(document: Document, id: LayerId): LayerId | null {
  const found = findLayer(document.root, id);
  if (found === null) return null;
  const parent = found.ancestors.at(-1);
  return parent?.id ?? null;
}

/* ------------------------------------------------------------------ */
/* 新建 / 删除                                                          */
/* ------------------------------------------------------------------ */

export function createAddLayerCommand(options: {
  layer: Layer;
  parentId: LayerId | null;
  index: number;
}): Command {
  const { layer, parentId, index } = options;
  return {
    kind: 'layer.add',
    label: `新建图层「${layer.name}」`,
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.insertLayer(parentId, layer, index);
    },
    invert() {
      return createRemoveLayerCommandRaw(layer, parentId, index);
    },
  };
}

/** 删除的内部构造：已知被删图层与其位置时使用（供 invert 复用）。 */
function createRemoveLayerCommandRaw(
  layer: Layer,
  parentId: LayerId | null,
  index: number,
): Command {
  return {
    kind: 'layer.remove',
    label: `删除图层「${layer.name}」`,
    // 逆操作要持有被删的整棵子树。子树只是结构引用，像素仍在 TileStore 里，
    // 所以这里的成本与图层数成正比、与像素量无关。
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.removeLayer(layer.id);
    },
    invert() {
      return createAddLayerCommand({ layer, parentId, index });
    },
  };
}

export function createRemoveLayerCommand(id: LayerId): Command {
  return {
    kind: 'layer.remove',
    label: '删除图层',
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.removeLayer(id);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      // 图层不存在时无从撤销，返回 null 而不是一条什么都不做的命令——
      // 历史里出现一条"撤销后什么都没变"的条目会让用户以为撤销坏了
      if (found === null) return null;
      return createAddLayerCommand({
        layer: found.layer,
        parentId: parentIdOf(document, id),
        index: found.index,
      });
    },
  };
}

/* ------------------------------------------------------------------ */
/* 重排                                                                 */
/* ------------------------------------------------------------------ */

export function createReorderLayerCommand(options: {
  id: LayerId;
  newParentId: LayerId | null;
  newIndex: number;
}): Command {
  const { id, newParentId, newIndex } = options;
  return {
    kind: 'layer.reorder',
    label: '调整图层顺序',
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.moveLayer(id, newParentId, newIndex);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      // 逆操作是"移回原来的父组与下标"
      return createReorderLayerCommand({
        id,
        newParentId: parentIdOf(document, id),
        newIndex: found.index,
      });
    },
  };
}

/* ------------------------------------------------------------------ */
/* 属性                                                                 */
/* ------------------------------------------------------------------ */

export function createRenameLayerCommand(id: LayerId, name: string): Command {
  return {
    kind: 'layer.rename',
    label: `重命名为「${name}」`,
    cost: STRUCTURAL_COST,
    record: { kind: 'layer.rename', args: { id, name } },
    apply(draft) {
      draft.setLayerName(id, name);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      return createRenameLayerCommand(id, found.layer.name);
    },
  };
}

export function createSetVisibleCommand(id: LayerId, visible: boolean): Command {
  return {
    kind: 'layer.visible',
    label: visible ? '显示图层' : '隐藏图层',
    cost: STRUCTURAL_COST,
    record: { kind: 'layer.visible', args: { id, visible } },
    apply(draft) {
      draft.setVisible(id, visible);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      return createSetVisibleCommand(id, found.layer.visible);
    },
  };
}

/**
 * 展开/收起一个组（图层面板的视觉状态）。不带 `record`——这是纯粹的
 * 面板查看便利，不是"参数性"的编辑动作，不值得进动作录制/批处理
 * 的白名单（ADR-0007 同一个分界线）。
 */
export function createSetCollapsedCommand(id: LayerId, collapsed: boolean): Command {
  return {
    kind: 'layer.collapsed',
    label: collapsed ? '收起组' : '展开组',
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.setCollapsed(id, collapsed);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null || found.layer.kind !== 'group') return null;
      return createSetCollapsedCommand(id, found.layer.collapsed);
    },
  };
}

export function createSetOpacityCommand(id: LayerId, opacity: number): Command {
  return {
    kind: 'layer.opacity',
    label: `不透明度 ${Math.round(opacity * 100)}%`,
    cost: STRUCTURAL_COST,
    record: { kind: 'layer.opacity', args: { id, opacity } },
    apply(draft) {
      draft.setOpacity(id, opacity);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      return createSetOpacityCommand(id, found.layer.opacity);
    },
  };
}

export function createSetBlendModeCommand(id: LayerId, blendMode: BlendMode): Command {
  return {
    kind: 'layer.blendMode',
    label: `混合模式 ${blendMode}`,
    cost: STRUCTURAL_COST,
    record: { kind: 'layer.blendMode', args: { id, blendMode } },
    apply(draft) {
      draft.updateLayer(id, (layer) => ({ ...layer, blendMode }));
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      return createSetBlendModeCommand(id, found.layer.blendMode);
    },
  };
}

export function createSetTransformCommand(id: LayerId, transform: Mat3): Command {
  return {
    kind: 'layer.transform',
    label: '变换图层',
    cost: STRUCTURAL_COST,
    record: { kind: 'layer.transform', args: { id, transform: { ...transform } } },
    apply(draft) {
      draft.setTransform(id, transform);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      // 存旧矩阵而不是算逆矩阵：逆矩阵在退化变换（缩放到 0）下不存在，
      // 而存旧值永远可行且精确
      return createSetTransformCommand(id, found.layer.transform);
    },
  };
}

/* ------------------------------------------------------------------ */
/* 编组                                                                 */
/* ------------------------------------------------------------------ */

/**
 * 把若干图层编入一个新建的组。
 *
 * 逆操作是解组：把组内图层放回原位并删除该组。**顺序很关键**——
 * 必须按原下标从小到大插回，否则前面的插入会挤动后面的下标。
 */
export function createGroupLayersCommand(options: {
  group: Extract<Layer, { kind: 'group' }>;
  memberIds: readonly LayerId[];
  parentId: LayerId | null;
  index: number;
}): Command {
  const { group, memberIds, parentId, index } = options;

  return {
    kind: 'layer.group',
    label: `编组 ${memberIds.length} 个图层`,
    cost: STRUCTURAL_COST * (memberIds.length + 1),
    apply(draft) {
      const members: Layer[] = [];
      for (const id of memberIds) {
        const found = findLayer(draft.current.root, id);
        if (found !== null) members.push(found.layer);
      }
      for (const id of memberIds) draft.removeLayer(id);
      draft.insertLayer(parentId, { ...group, children: members }, index);
    },
    invert(document) {
      // 记下每个成员原来的位置，解组时才能放回原处
      const positions = memberIds
        .map((id) => {
          const found = findLayer(document.root, id);
          if (found === null) return null;
          return { id, parentId: parentIdOf(document, id), index: found.index, layer: found.layer };
        })
        .filter((p): p is NonNullable<typeof p> => p !== null);

      return {
        kind: 'layer.ungroup',
        label: '取消编组',
        cost: STRUCTURAL_COST * (positions.length + 1),
        apply(draft) {
          draft.removeLayer(group.id);
          // 按原下标升序插回：降序或乱序会让后插入的挤动先插入的位置
          for (const p of [...positions].sort((a, b) => a.index - b.index)) {
            draft.insertLayer(p.parentId, p.layer, p.index);
          }
        },
        invert() {
          return createGroupLayersCommand(options);
        },
      };
    },
  };
}

/**
 * 设置图层样式列表（M3-14）。参数是结构性命令：改的只是效果参数对象，
 * 图层像素不动——与调整图层的「非破坏优先」是同一纪律。
 */
export function createSetEffectsCommand(id: LayerId, effects: readonly LayerEffect[]): Command {
  return {
    kind: 'layer.effects',
    label: '修改图层样式',
    cost: STRUCTURAL_COST,
    // `LayerEffect` 没有索引签名，但字段全是纯数据；转换是精确类型，不是 any
    record: { kind: 'layer.effects', args: { id, effects } as unknown as Readonly<Record<string, Json>> },
    apply(draft) {
      draft.setEffects(id, effects);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      return createSetEffectsCommand(id, found.layer.effects);
    },
  };
}

/* ------------------------------------------------------------------ */
/* 栅格化                                                               */
/* ------------------------------------------------------------------ */

/**
 * 把某个图层整体换成另一个同 id 的节点——栅格化就是"文字/形状图层 → 像素
 * 图层"这一种置换，逆操作是同一形状的置换换回原节点，所以栅格化命令与
 * 它自己的逆命令共用这一条通用原语，不需要额外定义一条 unrasterize。
 */
function createReplaceLayerCommand(id: LayerId, layer: Layer): Command {
  return {
    kind: 'layer.replace',
    label: `恢复图层「${layer.name}」`,
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.replaceLayer(id, layer);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      return createReplaceLayerCommand(id, found.layer);
    },
  };
}

/**
 * 栅格化图层（DESIGN.md §7.1 补充：图层面板右键"栅格化图层"，只对文字/
 * 形状图层可用）。`raster` 是调用方（`app/rasterize.ts`）已经准备好的
 * 替换节点——像素早写进了 TileStore（文字）或这一次新分配的 tiles
 * （形状），这里只管结构落地。
 */
export function createRasterizeLayerCommand(id: LayerId, raster: RasterLayer): Command {
  return {
    kind: 'layer.rasterize',
    label: `栅格化图层「${raster.name}」`,
    cost: STRUCTURAL_COST,
    apply(draft) {
      draft.replaceLayer(id, raster);
    },
    invert(document) {
      const found = findLayer(document.root, id);
      if (found === null) return null;
      return createReplaceLayerCommand(id, found.layer);
    },
  };
}

/* ------------------------------------------------------------------ */
/* 拼合图像 / 合并图层                                                    */
/* ------------------------------------------------------------------ */

/**
 * 拼合图像（DESIGN.md §7.1 补充：「图层→拼合图像」/右键菜单，参照
 * Photoshop）：删掉全部现有图层，只留调用方（`app/flatten.ts`）已经把
 * 全文档合成结果写好像素的这一个新图层。
 */
export function createFlattenImageCommand(layer: RasterLayer): Command {
  return {
    kind: 'layer.flatten',
    label: '拼合图像',
    cost: STRUCTURAL_COST,
    apply(draft) {
      for (const child of draft.current.root.children) draft.removeLayer(child.id);
      draft.insertLayer(null, layer, 0);
    },
    invert(document) {
      const original = document.root.children;
      return {
        kind: 'layer.unflatten',
        label: '撤销拼合图像',
        cost: STRUCTURAL_COST * (original.length + 1),
        apply(draft) {
          draft.removeLayer(layer.id);
          for (const [index, child] of original.entries()) draft.insertLayer(null, child, index);
        },
        invert() {
          return createFlattenImageCommand(layer);
        },
      };
    },
  };
}

/**
 * 合并选中的若干图层（DESIGN.md §7.1 补充：多选右键"合并图层"）为一个
 * 新的像素图层，落在这些图层里最上面那个的位置——跟 `groupActiveLayer`
 * （`app/layer-edit.ts`）的"插入位置"是同一个算法，只是这里删完不建组，
 * 建一个像素图层。`merged` 已经把参与合并的这些图层的合成结果写好像素。
 */
export function createMergeLayersCommand(options: {
  memberIds: readonly LayerId[];
  merged: RasterLayer;
  parentId: LayerId | null;
  index: number;
}): Command {
  const { memberIds, merged, parentId, index } = options;
  return {
    kind: 'layer.merge',
    label: `合并 ${memberIds.length} 个图层`,
    cost: STRUCTURAL_COST * (memberIds.length + 1),
    apply(draft) {
      for (const id of memberIds) draft.removeLayer(id);
      draft.insertLayer(parentId, merged, index);
    },
    invert(document) {
      const positions = memberIds
        .map((id) => {
          const found = findLayer(document.root, id);
          if (found === null) return null;
          return { id, parentId: parentIdOf(document, id), index: found.index, layer: found.layer };
        })
        .filter((p): p is NonNullable<typeof p> => p !== null);

      return {
        kind: 'layer.unmerge',
        label: '撤销合并图层',
        cost: STRUCTURAL_COST * (positions.length + 1),
        apply(draft) {
          draft.removeLayer(merged.id);
          // 按原下标升序插回：降序或乱序会让后插入的挤动先插入的位置
          for (const p of [...positions].sort((a, b) => a.index - b.index)) {
            draft.insertLayer(p.parentId, p.layer, p.index);
          }
        },
        invert() {
          return createMergeLayersCommand(options);
        },
      };
    },
  };
}
